import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Skeleton } from "@/shared/ui/skeleton";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import type { Language } from "@/shared/lib/language";
import {
  DATE_FORMATS,
  LANGUAGES,
  NUMBER_FORMATS,
  SETTING_FIELD_MAP,
  appliedSettingValues,
  changedSettingFields,
  dateFormatByWireDateFormat,
  exampleByNumberFormat,
  languageByWireLanguage,
  settingPatchFrom,
  type SettingField,
  type SettingValues,
  type UserSetting,
  type UserSettingPatch,
} from "@/shared/lib/user-setting";
import { NAV_CURRENCIES } from "@/shared/lib/nav-currency";
import { patchUserSetting, type UserProfile } from "@/features/auth/api/user";
import { useUserProfile, userProfileQueryKey } from "@/features/auth/model/use-user-profile";
import { useNotificationSubscriptionToggle } from "@/features/notification/hooks/use-notification-subscription-toggle";

const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  pt: "Português",
};

interface SettingSelectProps {
  field: SettingField;
  label: string;
  hint?: string;
  value: string;
  options: { value: string; label: string }[];
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

const SettingSelect = ({ field, label, hint, value, options, error, disabled, onChange }: SettingSelectProps) => {
  const id = `setting-${field}`;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        data-testid={`settings-${field}`}
        className={`w-full h-10 rounded-md border bg-background px-3 text-sm ${
          error ? "border-destructive" : "border-input"
        }`}
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <p id={`${id}-error`} className="text-xs text-destructive" data-testid={`settings-${field}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const Settings = () => {
  const { t, language, setLanguage } = useLanguage();
  const { dateFormat, setDateFormat } = useDateFormat();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profile = useUserProfile();
  const brokerOrderNotifications = useNotificationSubscriptionToggle("broker-order");
  const notificationState = brokerOrderNotifications.isSaving
    ? "saving"
    : brokerOrderNotifications.isLoading
      ? "loading"
      : !brokerOrderNotifications.isKnown
        ? "unknown"
        : brokerOrderNotifications.isEnabled
          ? "on"
          : "off";

  const stored = profile.setting;
  const applied: SettingValues | null = stored
    ? appliedSettingValues({ language, dateFormat }, stored)
    : null;
  const appliedSignature = applied ? Object.values(applied).join("|") : null;

  const [draft, setDraft] = useState<Partial<SettingValues>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [announcesSave, setAnnouncesSave] = useState(false);

  useEffect(() => {
    setDraft({});
  }, [appliedSignature]);

  useEffect(() => {
    if (!announcesSave) return;
    setAnnouncesSave(false);
    toast({ title: t("settings.saved") });
  }, [announcesSave, t, toast]);

  const values: SettingValues | null = applied ? { ...applied, ...draft } : null;
  const hasChanges = values != null && applied != null && changedSettingFields(values, applied).length > 0;

  const save = useMutation({
    mutationFn: (patch: UserSettingPatch) => patchUserSetting(profile.sessionId!, patch),
    onSuccess: (saved: UserSetting) => {
      if (saved.language) setLanguage(languageByWireLanguage[saved.language]);
      if (saved.date_format) setDateFormat(dateFormatByWireDateFormat[saved.date_format]);
      queryClient.setQueryData<UserProfile>(userProfileQueryKey(profile.sessionId), (current) =>
        current ? { ...current, setting: saved } : current,
      );
      setDraft({});
      setFieldErrors({});
      setAnnouncesSave(true);
    },
    onError: (err) => {
      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(err, SETTING_FIELD_MAP);
      setFieldErrors(backendFieldErrors);
      const message = isUnreadableResponseError(err) ? t("settings.saveUnreadable") : generalError;
      if (message) {
        toast({ title: message, variant: "destructive" });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userProfileQueryKey(profile.sessionId) });
    },
  });

  const changeField = (field: SettingField, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!values || !applied || !stored || !hasChanges || !profile.sessionId) return;
    save.mutate(settingPatchFrom(values, applied, stored));
  };

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t("settings.title")}</h1>
          <p className="text-muted-foreground">{t("settings.description")}</p>
        </div>

        <Card className="bg-card border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">{t("settings.preferences")}</h2>
          {values ? (
            <form className="space-y-4" data-testid="settings-form" onSubmit={handleSubmit} noValidate>
              <SettingSelect
                field="language"
                label={t("settings.language")}
                value={values.language}
                options={LANGUAGES.map((option) => ({ value: option, label: LANGUAGE_NAMES[option] }))}
                error={fieldErrors.language}
                disabled={save.isPending}
                onChange={(value) => changeField("language", value)}
              />
              <SettingSelect
                field="dateFormat"
                label={t("settings.dateFormat")}
                value={values.dateFormat}
                options={DATE_FORMATS.map((option) => ({ value: option, label: option }))}
                error={fieldErrors.dateFormat}
                disabled={save.isPending}
                onChange={(value) => changeField("dateFormat", value)}
              />
              <SettingSelect
                field="currency"
                label={t("settings.currency")}
                hint={t("settings.currencyHint")}
                value={values.currency}
                options={NAV_CURRENCIES.map((option) => ({ value: option, label: option }))}
                error={fieldErrors.currency}
                disabled={save.isPending}
                onChange={(value) => changeField("currency", value)}
              />
              <SettingSelect
                field="numberFormat"
                label={t("settings.numberFormat")}
                hint={t("settings.numberFormatHint")}
                value={values.numberFormat}
                options={NUMBER_FORMATS.map((option) => ({ value: option, label: exampleByNumberFormat[option] }))}
                error={fieldErrors.numberFormat}
                disabled={save.isPending}
                onChange={(value) => changeField("numberFormat", value)}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={!hasChanges || save.isPending} data-testid="settings-save">
                  {save.isPending ? t("settings.saving") : t("settings.save")}
                </Button>
              </div>
            </form>
          ) : profile.isPending ? (
            <div data-testid="settings-loading">
              <p role="status" aria-live="polite" className="sr-only" data-testid="settings-loading-status">
                {t("settings.loading")}
              </p>
              <div className="space-y-4" aria-hidden="true">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full" />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="settings-load-error">
              <p className="text-sm text-destructive" role="alert">
                {t("settings.loadFailed")}
              </p>
              <Button
                variant="outline"
                onClick={() => profile.refetch()}
                disabled={profile.isFetching}
                data-testid="settings-load-retry"
              >
                {t("settings.retry")}
              </Button>
            </div>
          )}
        </Card>

        <Card className="bg-card border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-1">{t("settings.notifications")}</h2>
          <p className="text-sm text-muted-foreground mb-4">{t("settings.notificationsScope")}</p>
          <div className="flex items-center gap-3">
            <Checkbox
              id="settings-notify-new-orders"
              data-testid="settings-notify-new-orders"
              data-subscription-state={notificationState}
              checked={brokerOrderNotifications.isKnown && brokerOrderNotifications.isEnabled}
              disabled={!brokerOrderNotifications.isKnown || brokerOrderNotifications.isSaving}
              aria-busy={brokerOrderNotifications.isSaving}
              aria-describedby={notificationState === "unknown" ? "settings-notify-load-error" : undefined}
              onCheckedChange={(checked) =>
                brokerOrderNotifications.setEnabled(checked === true, {
                  onError: () => toast({ title: t("settings.notificationsSaveFailed"), variant: "destructive" }),
                })
              }
            />
            <label htmlFor="settings-notify-new-orders" className="text-sm">
              {t("settings.notifyOnNewOrders")}
            </label>
            {notificationState === "saving" && (
              <span role="status" className="text-xs text-muted-foreground" data-testid="settings-notify-saving">
                {t("settings.saving")}
              </span>
            )}
          </div>
          {notificationState === "unknown" && (
            <div className="mt-3 space-y-3" data-testid="settings-notify-load-error-panel">
              <p id="settings-notify-load-error" className="text-sm text-destructive" role="alert" data-testid="settings-notify-load-error">
                {t("settings.notificationsLoadFailed")}
              </p>
              <Button
                variant="outline"
                onClick={() => brokerOrderNotifications.retry()}
                disabled={brokerOrderNotifications.isRetrying}
                data-testid="settings-notify-retry"
              >
                {t("settings.retry")}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
};

export default Settings;
